import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";

import * as Sentry from "@sentry/react";

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Typography
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

import { subcategories } from "@aclymatepackages/subcategories";

import ManualTransactionsInput from "../../inputs/emissions/ManualTransactionsInput";
import SmartUploadSourcePanel from "./SmartUploadSourcePanel";
import AllocationStep from "./AllocationStep";
import { duplicateNotice, matchedSummary } from "./SmartUploadReviewRow";

import {
  buildManualFallbackPrefill,
  manualLineFromTransaction,
  requirementsForTransaction
} from "../../../helpers/smartUploadManualFallback";
import {
  appendManualSmartUploadLine,
  commitSmartUploadLines,
  updateSmartUploadLines,
  uploadSmartUploadAnnotationThumbnail
} from "../../../helpers/smartUpload";
import { useCachedDisplayData } from "../../../helpers/firebase";

// "input: true" subcategories (@aclymatepackages/lists) plus
// upstream-leased-assets all resolve through resolveSubcategoryToTaxonomy --
// nothing is excluded today. Mirrors the backend's
// SMART_UPLOAD_MANUAL_EXCLUDED_SUBCATEGORIES (functions/utils/taxonomy/
// mapExtractedLineToTaxonomy.js), duplicated here the same way
// MANUAL_PASSTHROUGH_FIELDS is: the front and back ends can't share code
// directly, and the front-end copy only needs the key set, not the reasons.
const SMART_UPLOAD_MANUAL_EXCLUDED_SUBCATEGORIES = {};

const isPhysicalEntityContext = (task) =>
  ["offices", "vehicles"].includes(task?.entity_context?.entity_type);

const entityNameFor = (entities, entityId) =>
  entities.find(({ id }) => id === entityId)?.name || "this location";

const allocatedEntityFromTransaction = (transaction) => {
  if (transaction?.vehicle?.id) {
    return { entity_type: "vehicles", entity_id: transaction.vehicle.id };
  }
  if (transaction?.office?.id) {
    return { entity_type: "offices", entity_id: transaction.office.id };
  }
  return null;
};

// @aclymatepackages/fetch-aclymate-api doesn't throw on a non-2xx response --
// it resolves with whatever JSON body the API sent -- so update-lines/
// commit-lines failures have to be read back out of that body instead of
// relying on a rejected promise.
const COMMITTED_OUTCOMES = new Set(["committed", "already_committed"]);

const updateLinesFailureMessage = (response, lineId) => {
  if (response?.error) {
    return response.message || "We couldn't update this entry.";
  }
  const rejection = (response?.rejected || []).find((entry) => entry.lineId === lineId);
  return rejection ? rejection.message || "We couldn't update this entry." : null;
};

const commitLinesFailureMessage = (response, lineId) => {
  if (response?.error) {
    return response.message || "We couldn't save this entry.";
  }
  const result = (response?.results || []).find((entry) => entry.lineId === lineId);
  if (result && COMMITTED_OUTCOMES.has(result.outcome)) {
    return null;
  }
  return result?.reason
    ? `This entry couldn't be recorded yet (${result.reason}). Correct it above and save again.`
    : "We couldn't save this entry.";
};

const saveDisabledReason = ({
  transaction,
  requirements,
  allocatedEntity,
  requiresSlideoutConfirmation,
  isSlideoutTicked
}) => {
  if (!transaction?.date) {
    return "Enter a date to continue.";
  }
  if (requirements.requiresEntity && !allocatedEntity?.entity_id) {
    return `Add the ${
      requirements.entityType === "vehicles" ? "vehicle" : "building"
    } this belongs to.`;
  }
  if (requirements.requiresVendor && !transaction?.vendorId && !transaction?.vendorName) {
    return "Add the vendor for this expense.";
  }
  if (requiresSlideoutConfirmation && !isSlideoutTicked) {
    return "Confirm where these emissions belong.";
  }
  return null;
};

// Composes #378's existing pieces into the one guided manual-entry surface
// ticket #42 wires up: the source document, the familiar Add Emissions form
// (ManualTransactionsInput, redirected through onSaveTransactions instead of
// create-transactions), the slideout confirmation and AllocationStep #378
// already built, and the Skip/Keep-both duplicate UI #379's commit path can
// return. Props are deliberately stable (Out of Scope: ticket #389 builds
// snip-and-comment on top of this same dialog).
const SmartUploadManualEntryDialog = ({ companyId, task, row, line, onClose }) => {
  const [offices] = useCachedDisplayData("offices", []);
  const [vehicles] = useCachedDisplayData("vehicles", [["type", "==", "company"]]);
  const entities = useMemo(
    () => [...(offices || []), ...(vehicles || [])],
    [offices, vehicles]
  );

  const [clientEntryId, setClientEntryId] = useState(() => uuidv4());
  // Starts at the line this dialog was opened to replace, if any -- but
  // "Add another entry from this file" must not inherit it: a second,
  // unrelated manual entry stamped with the first line's replacesLineId
  // would wrongly inherit its activity_type_ai/period_start_final/
  // allocation_signal (appendManualLine.js's REPLACED_LINE_CARRY_FIELDS) and
  // can false-positive #379's duplicate check against it.
  const [replacesLineId, setReplacesLineId] = useState(line?.line_id);
  const [transaction, setTransaction] = useState(null);
  const [allocationOverride, setAllocationOverride] = useState(null);
  const [isSlideoutTicked, setIsSlideoutTicked] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [duplicateState, setDuplicateState] = useState(null);
  const [successState, setSuccessState] = useState(null);
  const [isResolvingDuplicate, setIsResolvingDuplicate] = useState(false);
  // Ticket #389: the captured region/comment/thumbnail, held as a draft
  // (thumbnailDataUrl, not yet uploaded) until save() commits it alongside
  // the rest of the manual line -- no separate submission path.
  const [annotation, setAnnotation] = useState(null);
  const [isSnippingActive, setIsSnippingActive] = useState(false);

  const { startingTransactionProps, suggestedSubcategories } = useMemo(
    () => buildManualFallbackPrefill({ task, row, line, entities }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [row?.id, line?.id, entities.length]
  );

  const requirements = requirementsForTransaction({ row, transaction: transaction || {} });
  const transactionEntity = allocatedEntityFromTransaction(transaction);
  const allocatedEntity =
    allocationOverride ||
    (transactionEntity?.entity_type === requirements.entityType ? transactionEntity : null) ||
    (requirements.requiresEntity && startingTransactionProps[
      requirements.entityType === "vehicles" ? "vehicle" : "office"
    ]
      ? {
          entity_type: requirements.entityType,
          entity_id: startingTransactionProps[
            requirements.entityType === "vehicles" ? "vehicle" : "office"
          ].id
        }
      : null);

  const requiresSlideoutConfirmation = isPhysicalEntityContext(task);
  const contextEntityName = entityNameFor(
    entities,
    task?.entity_context?.entity_id
  );

  const disabledReason = transaction
    ? saveDisabledReason({
        transaction,
        requirements,
        allocatedEntity,
        requiresSlideoutConfirmation,
        isSlideoutTicked: !requiresSlideoutConfirmation || isSlideoutTicked
      })
    : "Select a category to continue.";

  const resetForAnotherEntry = () => {
    setClientEntryId(uuidv4());
    setTransaction(null);
    setAllocationOverride(null);
    setErrorMessage(null);
    setDuplicateState(null);
    setSuccessState(null);
    setIsSlideoutTicked(false);
    setReplacesLineId(undefined);
    setAnnotation(null);
  };

  // Every non-success outcome throws so TransactionDetailsBlock keeps the
  // form open on the entered values -- only a committed/already_committed
  // result, or a reviewer's Skip, actually closes anything.
  const handleAppendResponse = (response) => {
    if (response?.reason === "slideout_prompt_required") {
      setIsSlideoutTicked(false);
      throw new Error("Confirm where these emissions belong, then save again.");
    }
    if (response?.error) {
      const message = response.message || "We couldn't save this entry.";
      setErrorMessage(message);
      throw new Error(message);
    }

    const { result, duplicateOf } = response;
    if (result?.reason === "duplicate_detected" || result?.reason === "duplicate_unresolved") {
      setDuplicateState({ duplicateOf, lineId: response.lineId });
      throw new Error("duplicate-pending-review");
    }
    if (result?.outcome === "committed" || result?.outcome === "already_committed") {
      setSuccessState({ entityName: contextEntityName });
      return response.lineId;
    }
    if (result?.reason) {
      const message = `This entry couldn't be recorded yet (${result.reason}). Correct it above and save again.`;
      setErrorMessage(message);
      throw new Error(message);
    }
    return response.lineId;
  };

  const uploadAnnotationThumbnail = async () => {
    try {
      return await uploadSmartUploadAnnotationThumbnail({
        companyId,
        taskDocId: task.id,
        rowId: row.id,
        clientEntryId,
        dataUrl: annotation.thumbnailDataUrl
      });
    } catch (error) {
      Sentry.captureException(error);
      const message = "We couldn't upload the flagged region. Please try again.";
      setErrorMessage(message);
      throw new Error(message);
    }
  };

  const save = async (builtTransaction) => {
    setErrorMessage(null);
    setIsSaving(true);
    try {
      const sourceAnnotation = annotation
        ? {
            rect: annotation.rect,
            page: annotation.page,
            comment: annotation.comment,
            thumbnail_gcs_path: await uploadAnnotationThumbnail()
          }
        : null;
      const manualLine = {
        ...manualLineFromTransaction({ transaction: builtTransaction }),
        ...(allocationOverride ? { allocated_entity_final: allocationOverride } : {}),
        ...(sourceAnnotation ? { source_annotation: sourceAnnotation } : {})
      };
      const response = await appendManualSmartUploadLine({
        companyId,
        taskDocId: task.id,
        rowId: row.id,
        clientEntryId,
        manualLine,
        replacesLineId,
        confirmSlideoutPrompt: requiresSlideoutConfirmation ? isSlideoutTicked : false
      });
      return handleAppendResponse(response);
    } finally {
      setIsSaving(false);
    }
  };

  // fetch-aclymate-api resolves instead of rejecting on a non-2xx response,
  // so both update-lines and commit-lines failures (an outright error, or a
  // per-line rejection/non-committed outcome embedded in a 200) have to be
  // checked explicitly -- otherwise "Keep both"/"Skip" show success and
  // reset the dialog even though nothing was actually recorded. On any
  // failure the duplicate notice stays up (never reset/closed) so the
  // reviewer can retry.
  const resolveDuplicate = async (resolution) => {
    setIsResolvingDuplicate(true);
    setErrorMessage(null);
    try {
      const updateResponse = await updateSmartUploadLines({
        companyId,
        taskDocId: task.id,
        rowId: row.id,
        updates: [
          {
            lineId: duplicateState.lineId,
            fields: {
              duplicate_resolution: resolution,
              ...(resolution === "skip" ? { review_state: "duplicate_skipped" } : {})
            }
          }
        ]
      });
      const updateFailure = updateLinesFailureMessage(updateResponse, duplicateState.lineId);
      if (updateFailure) {
        return setErrorMessage(updateFailure);
      }

      // Mirrors the committed-success path's own "Add another entry from
      // this file" button (resetForAnotherEntry) rather than closing
      // outright -- skipping a duplicate means this one entry isn't
      // recorded, not that the reviewer is done with the file, and the row
      // may still need further manual lines once hasLineTable (#42) takes
      // over its display.
      if (resolution === "skip") {
        return resetForAnotherEntry();
      }

      const commitResponse = await commitSmartUploadLines({
        companyId,
        taskDocId: task.id,
        lines: [{ rowId: row.id, lineId: duplicateState.lineId }]
      });
      const commitFailure = commitLinesFailureMessage(commitResponse, duplicateState.lineId);
      if (commitFailure) {
        return setErrorMessage(commitFailure);
      }

      setDuplicateState(null);
      return setSuccessState({ entityName: contextEntityName });
    } catch {
      return setErrorMessage("We couldn't reach the server -- check your connection and try again.");
    } finally {
      setIsResolvingDuplicate(false);
    }
  };

  const renderDuplicateNotice = () => (
    <Alert
      severity="warning"
      action={
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            disabled={isResolvingDuplicate}
            onClick={() => resolveDuplicate("skip")}
          >
            Skip
          </Button>
          <Button
            size="small"
            disabled={isResolvingDuplicate}
            onClick={() => resolveDuplicate("keep")}
          >
            Keep both
          </Button>
        </Box>
      }
    >
      <Typography variant="body2">
        {duplicateNotice({ duplicate_of: duplicateState.duplicateOf })}
      </Typography>
      {matchedSummary(duplicateState.duplicateOf?.matched) && (
        <Typography variant="caption" color="textSecondary">
          Matches: {matchedSummary(duplicateState.duplicateOf?.matched)}
        </Typography>
      )}
    </Alert>
  );

  const renderSuccessPanel = () => (
    <Alert severity="success">
      <Typography variant="body2">
        Saved and filed to {successState.entityName}.
      </Typography>
      <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
        <Button size="small" variant="outlined" onClick={resetForAnotherEntry}>
          Add another entry from this file
        </Button>
        <Button size="small" onClick={onClose}>
          Done
        </Button>
      </Box>
    </Alert>
  );

  const showAllocationStep =
    requirements.requiresEntity &&
    !(transactionEntity?.entity_type === requirements.entityType && transactionEntity?.entity_id);

  const availableSubcategories = useMemo(
    () =>
      subcategories.filter(
        ({ input, subcategory }) =>
          input && !SMART_UPLOAD_MANUAL_EXCLUDED_SUBCATEGORIES[subcategory]
      ),
    []
  );

  return (
    <Dialog
      open
      // Escape mid-snip must cancel the overlay only -- SnipOverlay handles
      // that cancellation itself; this just stops the same keypress from
      // also closing the whole dialog out from under it.
      onClose={(event, reason) => {
        if (reason === "escapeKeyDown" && isSnippingActive) {
          return;
        }
        onClose();
      }}
      fullWidth
      maxWidth="xl"
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6">Enter manually</Typography>
          </Box>
          <IconButton aria-label="Close manual entry" onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", gap: 2 }}>
          <SmartUploadSourcePanel
            row={row}
            line={line}
            onClose={onClose}
            annotation={annotation}
            onAnnotationChange={setAnnotation}
            onSnipModeChange={setIsSnippingActive}
            resetSignal={clientEntryId}
          />
          <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
            {successState && renderSuccessPanel()}
            {!successState && duplicateState && renderDuplicateNotice()}
            {!successState && !duplicateState && (
              <>
                {!!suggestedSubcategories.length && !transaction?.subcategory && (
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                    <Typography variant="caption" color="textSecondary">
                      Smart Upload suggested:
                    </Typography>
                    {suggestedSubcategories.map((label) => (
                      <Button key={label} size="small" variant="outlined" disabled>
                        {label}
                      </Button>
                    ))}
                  </Box>
                )}
                {requiresSlideoutConfirmation && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isSlideoutTicked}
                        onChange={(event) => setIsSlideoutTicked(event.target.checked)}
                      />
                    }
                    label={`These emissions belong to ${contextEntityName} — is that correct?`}
                  />
                )}
                {showAllocationStep && (
                  <AllocationStep
                    entityType={requirements.entityType}
                    resolution={{
                      entityId: allocatedEntity?.entity_id || null,
                      matchSource: allocatedEntity ? "matched" : "none",
                      candidates: (line?.allocation_candidates || []).map(
                        ({ entity_id: entityId, label }) => ({ entityId, label })
                      )
                    }}
                    onConfirm={({ id }) =>
                      setAllocationOverride({
                        entity_type: requirements.entityType,
                        entity_id: id
                      })
                    }
                    lineId={line?.line_id || row?.id}
                  />
                )}
                <ManualTransactionsInput
                  setDialogOpen={onClose}
                  startingTransactionProps={startingTransactionProps}
                  availableSubcategories={availableSubcategories}
                  inlineSubcategorySelector
                  closeOnSave={false}
                  onTransactionChange={setTransaction}
                  onSaveTransactions={save}
                />
              </>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        {!successState && !duplicateState && disabledReason && (
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="caption" color="textSecondary">
              {disabledReason}
            </Typography>
          </Box>
        )}
        {isSaving && <Typography variant="caption">Saving…</Typography>}
      </DialogActions>
    </Dialog>
  );
};

SmartUploadManualEntryDialog.propTypes = {
  companyId: PropTypes.string.isRequired,
  task: PropTypes.shape({
    id: PropTypes.string,
    entity_context: PropTypes.shape({ entity_id: PropTypes.string })
  }).isRequired,
  row: PropTypes.shape({ id: PropTypes.string }).isRequired,
  line: PropTypes.shape({
    id: PropTypes.string,
    line_id: PropTypes.string,
    allocation_candidates: PropTypes.arrayOf(
      PropTypes.shape({ entity_id: PropTypes.string, label: PropTypes.string })
    )
  }),
  onClose: PropTypes.func.isRequired
};

export default SmartUploadManualEntryDialog;
